// Public self-enrollment wizard client — Access Provider Phase 3, Task 11.
//
// Mirrors the route surface in apps/api/src/routes/enroll.ts, mounted
// unauthenticated at `/api/enroll` (see that router's header comment:
// "Deliberately carries NO authMiddleware/auditMiddleware"). Every call
// here omits `token` from `apiRequest` on purpose — a prospective coworker
// enrolling has no JWT to present. Trust is instead anchored on the
// per-company `companyToken` (start) and the per-enrollment `stepToken`
// minted by `start()` (verify/resend/face).
import { apiRequest } from './client'
import type { EnrollmentStatus } from '@hub-wifi/shared'

export interface StartEnrollmentInput {
  name: string
  email: string
  mobilePhone: string
  taxIdCountry: string
  taxIdType: string
  taxIdValue: string
  birthDate?: string | null
}

export interface StartEnrollmentResult {
  enrollmentId: string
  stepToken: string
  recapture: boolean
  /** False when the company skips the emailed code — go straight to the photo step. */
  emailVerificationRequired: boolean
}

/** POST /api/enroll/:companyToken — start a new enrollment. */
export function startEnrollment(
  companyToken: string,
  body: StartEnrollmentInput,
): Promise<StartEnrollmentResult> {
  return apiRequest<StartEnrollmentResult>(`/api/enroll/${encodeURIComponent(companyToken)}`, {
    method: 'POST',
    body,
  })
}

/** POST /api/enroll/:id/verify — confirm the emailed 6-digit code. */
export function verifyEnrollment(
  enrollmentId: string,
  stepToken: string,
  code: string,
): Promise<{ status: 'verified' }> {
  return apiRequest<{ status: 'verified' }>(
    `/api/enroll/${encodeURIComponent(enrollmentId)}/verify`,
    {
      method: 'POST',
      body: { stepToken, code },
    },
  )
}

/** POST /api/enroll/:id/resend — resend the 6-digit code. */
export function resendEnrollment(enrollmentId: string, stepToken: string): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>(`/api/enroll/${encodeURIComponent(enrollmentId)}/resend`, {
    method: 'POST',
    body: { stepToken },
  })
}

/** POST /api/enroll/:id/face — submit the consented selfie. */
export function submitEnrollmentFace(
  enrollmentId: string,
  stepToken: string,
  photoBase64: string,
  consent: boolean,
): Promise<{ status: EnrollmentStatus }> {
  return apiRequest<{ status: EnrollmentStatus }>(
    `/api/enroll/${encodeURIComponent(enrollmentId)}/face`,
    {
      method: 'POST',
      body: { stepToken, photoBase64, consent },
    },
  )
}
